import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import { FormHelperText } from '@mui/material';
import { Tooltip } from '@material-ui/core';

import { controlledDatePicker } from 'components/ReactHookForm/controlledDatePicker';
import { gray2 } from 'utils/color-styles';

import { FormFieldValues, ActionFormProps } from './types';

const StartingAtInput = controlledDatePicker<FormFieldValues>()('startingAt');
const EndingAtInput = controlledDatePicker<FormFieldValues>()('endingAt');

export const DatesStepContent = (props: ActionFormProps): JSX.Element => {
  const isEndingAtAfterStartingAt = (date: Moment | string | null) => {
    const endingAt = typeof date === 'string' ? moment.utc(date) : date;
    const startingAt =
      props.formMethods?.getValues().startingAt &&
      moment.utc(props.formMethods?.getValues().startingAt);

    if (endingAt && startingAt && endingAt.isBefore(startingAt.startOf('day'), 'day')) {
      return 'End date should be after the start date';
    }

    return true;
  };

  const [tooltip, setTooltip] = useState('');
  const watchFields = props.formMethods.watch(['startingAt', 'endingAt']);

  const getTooltip = () => {
    const selectedStartingDate = props.formMethods?.getValues().startingAt;
    const selectedEndingDate = props.formMethods?.getValues().endingAt;
    const timeZone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;
    const timeZoneMessage = `(UTC${moment().format('Z')} ${timeZone})`;

    if (!selectedStartingDate && !selectedEndingDate) {
      return '';
    }

    const startDateString =
      selectedStartingDate &&
      moment(selectedStartingDate).startOf('day').format('YYYY-MM-DD HH:mm');

    const endDateString =
      selectedEndingDate && moment(selectedEndingDate).endOf('day').format('YYYY-MM-DD HH:mm');

    const firstStringPart = `In your current location ${timeZoneMessage},`;

    if (selectedStartingDate && !selectedEndingDate) {
      return `${firstStringPart} this Action will start at ${startDateString}.`;
    }

    if (!selectedStartingDate && selectedEndingDate) {
      return `${firstStringPart} this Action will end at ${endDateString}.`;
    }

    return `${firstStringPart} this Action will start at ${startDateString} and end at ${endDateString}.`;
  };

  useEffect(() => {
    setTooltip(getTooltip());
  }, [watchFields]);

  return (
    <>
      <p style={{ marginTop: 0 }}>
        If no start date is set, this Action will be available via the Actions tab as soon as it is
        &quot;Published&quot;.
      </p>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
        <StartingAtInput
          controllerProps={{ control: props.formMethods.control }}
          label="Starting at"
          inputFormat="yyyy-MM-DD"
        />

        <EndingAtInput
          controllerProps={{
            control: props.formMethods.control,
            rules: {
              validate: {
                minDate: isEndingAtAfterStartingAt,
              },
            },
          }}
          label="Ending at"
          inputFormat="yyyy-MM-DD"
          minDate={
            props.formMethods?.getValues().startingAt
              ? moment.utc(props.formMethods?.getValues().startingAt)
              : undefined
          }
        />
        <Tooltip title={tooltip}>
          <div style={{ marginLeft: 10, fontSize: 11, color: gray2 }}>
            UTC{moment().format('Z')}
          </div>
        </Tooltip>
        <FormHelperText error>
          {props.formMethods.getFieldState('endingAt').error?.message}
        </FormHelperText>
      </div>
    </>
  );
};
